import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Facebook, Linkedin, Link2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { api } from "../api";
import { SITE, IMG } from "../data/siteData";

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-label="X">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-obsidian pt-40 flex justify-center">
        <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (post === null) {
    return (
      <div className="min-h-screen bg-obsidian pt-40 pb-24 text-center px-6" data-testid="blog-not-found">
        <h1 className="font-heading text-3xl font-bold text-white mb-4">Article not found</h1>
        <Link to="/blog" className="text-brand font-heading font-bold hover:underline">Back to the blog</Link>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const encoded = encodeURIComponent(shareUrl);
  const titleEnc = encodeURIComponent(post.title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — share it anywhere!");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  return (
    <div data-testid="blog-post-page">
      <Helmet>
        <title>{`${post.title} | Chinook K9 Blog`}</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:type" content="article" />
        {post.cover_image && <meta property="og:image" content={`${window.location.origin}${post.cover_image}`} />}
        <link rel="canonical" href={`https://chinookk9.com/blog/${post.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta_description || post.excerpt,
            datePublished: post.created_at,
            author: { "@type": "Organization", name: "Chinook K9" },
            publisher: { "@type": "Organization", name: "Chinook K9" },
          })}
        </script>
      </Helmet>

      <section className="bg-obsidian pt-36 pb-12 border-b border-white/10" data-testid="blog-post-hero">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-brand text-sm font-medium transition-colors mb-8" data-testid="blog-back-link">
              <ArrowLeft size={15} /> All articles
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter text-white leading-[1.1]">
              {post.title}
            </h1>
            <p className="mt-4 text-white/50 text-sm">
              {new Date(post.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })} · Chinook K9 Training Team
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-12" data-testid="blog-post-body">
        <div className="max-w-3xl mx-auto px-6">
          {post.cover_image && (
            <Reveal>
              <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl border border-white/10 mb-12 object-cover max-h-[440px]" data-testid="blog-cover" />
            </Reveal>
          )}
          {(post.sections || []).map((s, i) => (
            <Reveal key={i} delay={0.05} className="mb-10" data-testid={`blog-section-${i}`}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">{s.heading}</h2>
              {s.body.split("\n\n").map((p, j) => (
                <p key={j} className="text-white/75 leading-relaxed mb-4">{p}</p>
              ))}
            </Reveal>
          ))}

          <Reveal className="mt-14 pt-8 border-t border-white/10" data-testid="blog-share">
            <p className="text-xs font-heading font-bold tracking-widest uppercase text-white/40 mb-4">Share this article</p>
            <div className="flex flex-wrap gap-3">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`} target="_blank" rel="noreferrer" data-testid="share-facebook"
                className="flex items-center gap-2 bg-[#161616] border border-white/15 rounded-full px-5 py-2.5 text-sm text-white/80 hover:border-brand hover:text-brand transition-colors">
                <Facebook size={15} /> Facebook
              </a>
              <a href={`https://twitter.com/intent/tweet?url=${encoded}&text=${titleEnc}`} target="_blank" rel="noreferrer" data-testid="share-x"
                className="flex items-center gap-2 bg-[#161616] border border-white/15 rounded-full px-5 py-2.5 text-sm text-white/80 hover:border-brand hover:text-brand transition-colors">
                <XIcon size={14} /> X
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`} target="_blank" rel="noreferrer" data-testid="share-linkedin"
                className="flex items-center gap-2 bg-[#161616] border border-white/15 rounded-full px-5 py-2.5 text-sm text-white/80 hover:border-brand hover:text-brand transition-colors">
                <Linkedin size={15} /> LinkedIn
              </a>
              <button onClick={copyLink} data-testid="share-copy"
                className="flex items-center gap-2 bg-[#161616] border border-white/15 rounded-full px-5 py-2.5 text-sm text-white/80 hover:border-brand hover:text-brand transition-colors">
                <Link2 size={15} /> Copy Link
              </button>
            </div>
          </Reveal>

          <Reveal className="mt-12 bg-[#121212] border border-brand/30 rounded-2xl p-8 text-center" data-testid="blog-cta">
            <h3 className="font-heading text-2xl font-bold text-white mb-3">Ready to train with us?</h3>
            <p className="text-white/60 text-sm mb-6">Tell us about your dog and we'll recommend the right program.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" data-testid="blog-contact-cta" className="bg-brand text-white font-heading font-bold px-7 py-3 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Book Now! <ArrowRight size={15} />
              </Link>
              <a href={SITE.phoneHref} className="border border-white/25 text-white font-heading font-semibold px-7 py-3 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <Phone size={15} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
